// src/components/TaskForm.tsx
import React, { useState } from "react";
//import TaskForm from "./TaskForm";
import TaskDescriptionEditor from "./TaskDescriptionEditor";
import TaskNotesEditor from "./TaskNotesEditor";
import { createTask, updateTask, deleteTask } from "../api/tasks";
import { PersianDate } from "../utils/PersianDate";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTrash } from "@fortawesome/free-solid-svg-icons";
import '../styles/TaskCard.css';
import TaskForm from "./AddTaskForm";

interface TaskNote {
    id: number;
    content: string;
    createdById: number;
    createdAt: string;
    updatedAt?: string;
    isDone?: boolean;
}

interface Task {
    id: number;
    title: string;
    description: string;
    isDone: boolean;
    createdById?: number | null;
    notes: TaskNote[];
    createdAt: string;
}

interface TaskCardProps {
    projectId: number;
    tasks: Task[];
    currentUserId: number;
    onTasksUpdate: (updatedTasks: Task[]) => void;
}

export default function TaskCard({
    projectId,
    tasks: initialTasks,
    currentUserId,
    onTasksUpdate,
}: TaskCardProps) {
    const [showForm, setShowForm] = useState(false);
    const [showDoneTasks, setShowDoneTasks] = useState(true);
    const [tasks, setTasks] = useState<Task[]>(initialTasks);
    const [deletingId, setDeletingId] = useState<number | null>(null); // برای لودینگ

    React.useEffect(() => {
        setTasks(initialTasks);
    }, [initialTasks]);

    const handleAddTask = async (title: string, description?: string) => {
        try {
            const newTask = await createTask(projectId ,title , description );
            const updated = [...tasks, newTask];
            setTasks(updated);
            onTasksUpdate(updated);
            setShowForm(false);
        } catch (err: any) {
            alert(`خطا در افزودن تسک: ${err.message}`);
        }
    };

    const handleToggleDone = async (task: Task, checked: boolean) => {
        try {
            const payload = { ...task, isDone: checked };
            const saved = await updateTask(projectId, task.id, payload);
            const updated = tasks.map((t) => (t.id === task.id ? saved : t));
            setTasks(updated);
            onTasksUpdate(updated);
        } catch (err: any) {
            alert(`خطا در بروزرسانی وضعیت: ${err.message}`);
        }
    };

    const handleDeleteTask = async (taskId: number) => {
        const task = tasks.find((t) => t.id === taskId);
        if (!task) return;

        const confirmed = window.confirm(
            `آیا از حذف تسک زیر اطمینان دارید؟\n\nعنوان: ${task.title}\n\nاین عمل قابل بازگشت نیست.`
        );
        if (!confirmed) return;

        setDeletingId(taskId);
        try {
            await deleteTask(projectId, taskId);
            const updated = tasks.filter((t) => t.id !== taskId);
            setTasks(updated);
            onTasksUpdate(updated);
        } catch (err: any) {
            if (err.message.includes("403")) {
                alert("شما اجازه حذف این تسک را ندارید.");
            } else if (err.message.includes("404")) {
                alert("تسک قبلاً حذف شده یا وجود ندارد.");
            } else {
                alert(`خطا در حذف تسک: ${err.message}`);
            }
        } finally {
            setDeletingId(null);
        }
    };

    const handleUpdateDescription = async (taskId: number, newDesc: string) => {
        const task = tasks.find((t) => t.id === taskId);
        if (!task) return;

        try {
            const payload = { ...task, description: newDesc };
            const saved = await updateTask(projectId, taskId, payload);
            const updated = tasks.map((t) => (t.id === taskId ? saved : t));
            setTasks(updated);
            onTasksUpdate(updated);
        } catch (err: any) {
            alert(`خطا در ذخیره توضیحات: ${err.message}`);
        }
    };

    const handleNotesChanged = (taskId: number, newNotes: TaskNote[]) => {
        const updated = tasks.map((task) =>
            task.id === taskId ? { ...task, notes: newNotes } : task
        );
        setTasks(updated);
        onTasksUpdate(updated);
    };

    const visibleTasks = (!showDoneTasks ? tasks.filter((t) => !t.isDone) : tasks)
        .slice()
        .sort((a, b) => {
            if (a.isDone !== b.isDone) return a.isDone ? 1 : -1;
            const aTime = a.createdAt ? new Date(a.createdAt).getTime() : 0;
            const bTime = b.createdAt ? new Date(b.createdAt).getTime() : 0;
            return bTime - aTime;
        });

    return (
        <>
            <div style={{ display: "flex", gap: 12, marginRight: 12, marginBottom: 12, alignItems: "center" }}>
                <input
                    type="checkbox"
                    style={{ transform: "scale(2.5)", marginRight: 8 }}
                    checked={showDoneTasks}
                    onChange={(e) => setShowDoneTasks(e.target.checked)}
                />
                <label style={{ marginLeft: 20, fontSize: 16 }}>نمایش انجام‌شده‌ها</label>
                <button onClick={() => setShowForm(!showForm)}>
                    {showForm ? "انصراف" : "افزودن تسک"}
                </button>
            </div>

            {showForm && <TaskForm projectId={projectId} onSubmit={handleAddTask} />}

            <div style={{ marginTop: 20 }}>
                {visibleTasks.map((t) => (
                    <div
                        key={t.id}
                        style={{
                            border: "1px solid #ddd",
                            padding: 16,
                            marginBottom: 16,
                            borderRadius: 8,
                            background: "var(--app-card-bg-color)",
                            boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
                            position: "relative",
                        }}
                    >
                        <div className={t.isDone ? "taskCard-done" : "taskCard"}>
                            <div className="taskHeader" style={{ display: "flex", alignItems: "flex-start", gap: 12 }}>
                                <input
                                    type="checkbox"
                                    style={{ transform: "scale(1.6)", marginTop: 4 }}
                                    checked={t.isDone}
                                    onChange={(e) => handleToggleDone(t, e.target.checked)}
                                />
                                <div style={{ flex: 1 }}>
                                    <strong style={{ fontSize: 18, display: "block", marginBottom: 4 }}>
                                        {t.title}
                                    </strong>
                                    <div style={{ fontSize: 12, marginBottom: 8 }}>                                       
                                        {new PersianDate(new Date(t.createdAt)).toString("TDPD dd MMMM ساعت HH:mm")}
                                    </div>
                                </div>

                                 <button
                                    onClick={() => handleDeleteTask(t.id)}
                                    disabled={deletingId === t.id}
                                    style={{
                                        background: "none",
                                        border: "none",
                                        cursor: deletingId === t.id ? "not-allowed" : "pointer",
                                        padding: "8px",
                                        borderRadius: "6px",
                                        transition: "all 0.2s",
                                    }}
                                    onMouseEnter={(e) => {
                                        if (deletingId !== t.id) e.currentTarget.style.backgroundColor = "#fee";
                                    }}
                                    onMouseLeave={(e) => {
                                        e.currentTarget.style.backgroundColor = "transparent";
                                    }}
                                    title="حذف تسک"
                                >
                                    {deletingId === t.id ? (
                                        <span style={{ color: "#ce1717ff", fontSize: 14 }}>در حال حذف...</span>
                                    ) : (
                                        <FontAwesomeIcon
                                            icon={faTrash}
                                            style={{
                                                fontSize: 18,
                                                color: "#e74c3c",
                                                transition: "transform 0.2s",
                                            }}
                                            onMouseEnter={(e) => {
                                                if (deletingId !== t.id)
                                                    e.currentTarget.style.transform = "scale(1.2)";
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = "scale(1)";
                                            }}
                                        />
                                    )}
                                </button>
                            </div>

                            <div style={{ marginTop: 12 }}>
                                <TaskDescriptionEditor
                                    description={t.description}
                                    onSave={(d) => handleUpdateDescription(t.id, d)}
                                />

                                <TaskNotesEditor
                                    projectId={projectId}
                                    taskId={t.id ?? t.id}  // حتماً اینو داشته باش!
                                    notes={t.notes ?? []}
                                    onNotesChanged={(newNotes) => handleNotesChanged(t.id, newNotes)}
                                />
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </>
    );
}